import { useState } from 'react'
import {
  MAX_GRID,
  MIN_GRID,
  addCell,
  canMerge,
  cellAt,
  mergeCells,
  moveCell,
  removeCell,
  resizeGrid,
  swapCells,
  unmergeCell,
  updateCell,
} from '../data/layout'
import type { Category, GridCell, GridLayout } from '../data/types'

interface Props {
  layout: GridLayout
  categories: Category[]
  onChange: (layout: GridLayout) => void
}

/**
 * Direct manipulation of the Now grid: resize it, drag buttons around, merge two
 * into one bigger target, split one into two stacked halves.
 */
export function LayoutEditor({ layout, categories, onChange }: Props) {
  const [selected, setSelected] = useState<string | null>(null)
  const [dragging, setDragging] = useState<string | null>(null)

  const selectedCell = layout.cells.find((cell) => cell.id === selected) ?? null
  const mergeable = selectedCell
    ? layout.cells.filter((cell) => cell.id !== selectedCell.id && canMerge(selectedCell, cell))
    : []

  function drop(column: number, row: number) {
    if (!dragging) return
    const target = cellAt(layout, column, row)
    onChange(
      target && target.id !== dragging
        ? swapCells(layout, dragging, target.id)
        : moveCell(layout, dragging, column, row),
    )
    setDragging(null)
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <Stepper
          label="Columns"
          value={layout.columns}
          onChange={(columns) => onChange(resizeGrid(layout, columns, layout.rows))}
        />
        <Stepper
          label="Rows"
          value={layout.rows}
          onChange={(rows) => onChange(resizeGrid(layout, layout.columns, rows))}
        />
      </div>

      <div
        className="grid max-w-sm gap-1.5"
        style={{
          gridTemplateColumns: `repeat(${layout.columns}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${layout.rows}, 56px)`,
        }}
      >
        {/* Empty squares first, so a dragged cell can be dropped onto them. */}
        {Array.from({ length: layout.columns * layout.rows }, (_, index) => {
          const column = index % layout.columns
          const row = Math.floor(index / layout.columns)
          if (cellAt(layout, column, row)) return null
          return (
            <button
              key={`empty-${column}-${row}`}
              type="button"
              onDragOver={(event) => event.preventDefault()}
              onDrop={() => drop(column, row)}
              onClick={() => onChange(addCell(layout, categories[0]?.id ?? ''))}
              className="rounded-lg border border-dashed border-line text-[12px] text-faint transition-colors duration-150 hover:border-line-strong hover:text-dim"
              style={{ gridColumn: column + 1, gridRow: row + 1 }}
            >
              +
            </button>
          )
        })}

        {layout.cells.map((cell) => (
          <div
            key={cell.id}
            draggable
            onDragStart={() => setDragging(cell.id)}
            onDragEnd={() => setDragging(null)}
            onDragOver={(event) => event.preventDefault()}
            onDrop={() => drop(cell.column, cell.row)}
            onClick={() => setSelected(cell.id === selected ? null : cell.id)}
            className={`flex cursor-grab flex-col justify-center gap-0.5 rounded-lg border p-1 text-center transition-colors duration-150 ${
              cell.id === selected ? 'border-accent bg-raised' : 'border-line bg-surface hover:border-line-strong'
            }`}
            style={{
              gridColumn: `${cell.column + 1} / span ${cell.colSpan}`,
              gridRow: `${cell.row + 1} / span ${cell.rowSpan}`,
            }}
          >
            {cell.categoryIds.map((categoryId) => {
              const category = categories.find((candidate) => candidate.id === categoryId)
              return (
                <span
                  key={categoryId}
                  className="truncate rounded px-1 text-[12px] leading-tight font-medium"
                  style={{
                    color: category?.color ?? 'var(--tg-faint)',
                    backgroundColor: category
                      ? `color-mix(in oklab, ${category.color} 16%, transparent)`
                      : 'transparent',
                  }}
                >
                  {category?.shortLabel ?? categoryId}
                </span>
              )
            })}
          </div>
        ))}
      </div>

      {selectedCell ? (
        <CellControls
          cell={selectedCell}
          categories={categories}
          mergeable={mergeable}
          onChange={onChange}
          layout={layout}
          onDeselect={() => setSelected(null)}
        />
      ) : (
        <p className="text-[12px] leading-relaxed text-faint">
          Click a button to edit it, drag one onto another to swap them, or drag onto an empty
          square to move it there.
        </p>
      )}
    </div>
  )
}

function CellControls({
  cell,
  categories,
  mergeable,
  layout,
  onChange,
  onDeselect,
}: {
  cell: GridCell
  categories: Category[]
  mergeable: GridCell[]
  layout: GridLayout
  onChange: (layout: GridLayout) => void
  onDeselect: () => void
}) {
  const split = cell.categoryIds.length === 2
  const merged = cell.colSpan > 1 || cell.rowSpan > 1

  function setCategory(slot: 0 | 1, categoryId: string) {
    const ids = [...cell.categoryIds] as string[]
    if (categoryId === '') {
      if (slot === 1) ids.pop()
      else ids.shift()
    } else {
      ids[slot] = categoryId
    }
    const kept = ids.filter(Boolean)
    if (kept.length === 0) {
      onChange(removeCell(layout, cell.id))
      onDeselect()
      return
    }
    onChange(
      updateCell(layout, cell.id, {
        categoryIds:
          kept.length === 2 ? [kept[0] as string, kept[1] as string] : [kept[0] as string],
      }),
    )
  }

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-line-strong bg-surface p-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={cell.categoryIds[0] ?? ''}
          categories={categories}
          onChange={(id) => setCategory(0, id)}
        />
        {split ? (
          <Select
            value={cell.categoryIds[1] ?? ''}
            categories={categories}
            onChange={(id) => setCategory(1, id)}
          />
        ) : null}
      </div>

      <div className="flex flex-wrap gap-1.5">
        <Action
          onClick={() =>
            split
              ? setCategory(1, '')
              : setCategory(1, categories.find((c) => c.id !== cell.categoryIds[0])?.id ?? '')
          }
        >
          {split ? 'Unsplit' : 'Split in two'}
        </Action>

        {merged ? (
          <Action onClick={() => onChange(unmergeCell(layout, cell.id))}>Unmerge</Action>
        ) : null}

        {mergeable.map((other) => (
          <Action key={other.id} onClick={() => onChange(mergeCells(layout, cell.id, other.id))}>
            Merge {directionOf(cell, other)}
          </Action>
        ))}

        <Action
          onClick={() => {
            onChange(removeCell(layout, cell.id))
            onDeselect()
          }}
          danger
        >
          Remove
        </Action>
      </div>
    </div>
  )
}

function directionOf(cell: GridCell, other: GridCell): string {
  if (other.column >= cell.column + cell.colSpan) return 'right'
  if (other.column + other.colSpan <= cell.column) return 'left'
  if (other.row >= cell.row + cell.rowSpan) return 'down'
  return 'up'
}

function Select({
  value,
  categories,
  onChange,
}: {
  value: string
  categories: Category[]
  onChange: (categoryId: string) => void
}) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="min-w-0 rounded-md border border-line bg-bg px-2 py-1 text-[13px] outline-none focus:border-line-strong"
    >
      <option value="">none</option>
      {categories.map((category) => (
        <option key={category.id} value={category.id}>
          {category.shortLabel}
        </option>
      ))}
    </select>
  )
}

function Action({
  onClick,
  danger,
  children,
}: {
  onClick: () => void
  danger?: boolean
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md border px-2 py-1 text-[12px] font-medium transition-colors duration-150 ${
        danger
          ? 'border-danger/40 text-danger hover:bg-danger/10'
          : 'border-line text-dim hover:border-line-strong hover:text-text'
      }`}
    >
      {children}
    </button>
  )
}

function Stepper({
  label,
  value,
  onChange,
}: {
  label: string
  value: number
  onChange: (value: number) => void
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[12px] font-medium tracking-wide text-faint uppercase">{label}</span>
      <div className="flex items-center rounded-md border border-line">
        <StepButton onClick={() => onChange(value - 1)} disabled={value <= MIN_GRID}>
          −
        </StepButton>
        <span className="w-6 text-center text-[14px] font-medium tnum">{value}</span>
        <StepButton onClick={() => onChange(value + 1)} disabled={value >= MAX_GRID}>
          +
        </StepButton>
      </div>
    </div>
  )
}

function StepButton({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void
  disabled: boolean
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex size-6 items-center justify-center text-dim transition-colors duration-150 hover:text-text disabled:opacity-25"
    >
      {children}
    </button>
  )
}
